import React, { useMemo } from 'react';
import classNames from 'classnames';

import ProductCardContainer from 'components/Common/Card/ProductCard/ProductCardContainer/ProductCardContainer';
import ProductCardColors from 'components/Common/Card/ProductCard/ProductCardColors/ProductCardColors';
import ProductCardDetails from 'components/Common/Card/ProductCard/ProductCardDetails/ProductCardDetails';
import ProductCardDiscount from 'components/Common/Card/ProductCard/ProductCardDiscount/ProductCardDiscount';
import ProductCardImage, {
  ProductImage,
} from 'components/Common/Card/ProductCard/ProductCardImage/ProductCardImage';
import ProductCardLabel from 'components/Common/Card/ProductCard/ProductCardLabel/ProductCardLabel';
import ProductCardOverlay from 'components/Common/Card/ProductCard/ProductCardOverlay/ProductCardOverlay';
import ProductCardPrice from 'components/Common/Card/ProductCard/ProductCardPrice/ProductCardPrice';
import ProductCardSizes from 'components/Common/Card/ProductCard/ProductCardSizes/ProductCardSizes';
import ProductCardStickers from 'components/Common/Card/ProductCard/ProductCardStickers/ProductCardStickers';
import ProductCardLink from 'components/Common/Card/ProductCard/ProductCardLink/ProductCardLink';
import { useScrollIntoView } from 'hooks/useScrollIntoView';
import { getUri } from 'services/generic';
import {
  getCommercialOperationSticker,
  getDiscountPercentage,
  getName,
  getMainSticker,
  getSubSticker1,
  getSubSticker2,
  getBrandName,
  getFinalPrice,
  hasUniqPrice,
  getDiscountPrice,
  getSizesList,
  getLotQuantity,
} from 'services/product/attributes';
import { formatSizesList } from 'services/product/formatter';
import {
  getPreferredUrl,
  getPreferredVisuelBack,
  getPreferredVisuelDetail,
  getPreferredVisuelFace,
} from 'services/listing/product';
import {
  ProductAttributesType,
  ProductType,
} from 'types/ProductListing/ProductListing';

import styles from './CategoryProduct.module.scss';

type PropsType = {
  product: ProductType;
  onClick: React.MouseEventHandler<HTMLAnchorElement> | undefined;
  scrollInto?: boolean;
  onScroll?: () => void;
  className?: string;
  forceSkuTargeting?: boolean;
  preferCurvy: boolean;
  imageRatio?: string;
};

export default function CategoryProduct({
  product: { attributes, metadata, url },
  onClick,
  scrollInto,
  onScroll,
  className,
  forceSkuTargeting = false,
  preferCurvy,
  imageRatio,
}: PropsType): JSX.Element {
  const scrollRef = useScrollIntoView(onScroll);
  const { colorSwatch } = metadata;
  const discountPercentage = getDiscountPercentage(attributes);
  const sizesList = !preferCurvy && getSizesList(attributes);
  const productName = getName(attributes);
  const linkTo = getUri(getPreferredUrl(url, forceSkuTargeting, preferCurvy));
  const productCardImageProps = imageRatio
    ? { imageProps: { ratio: imageRatio } }
    : {};

  const images = useMemo(() => {
    return getProductImages(
      attributes,
      preferCurvy,
      productName,
      linkTo,
      onClick
    );
  }, [productName, attributes, preferCurvy, linkTo, onClick]);

  return (
    <ProductCardContainer
      className={classNames(styles.container, className)}
      ref={scrollInto ? scrollRef : undefined}
    >
      <ProductCardImage
        classes={{ container: styles.imageContainer, arrow: styles.imageArrow }}
        images={images}
        {...productCardImageProps}
      >
        {parseInt(discountPercentage) > 0 && (
          <ProductCardDiscount
            discountPercentage={discountPercentage}
            className={styles.cardDiscount}
          />
        )}
        <ProductCardStickers
          mainSticker={getMainSticker(attributes)}
          subSticker1={getSubSticker1(attributes)}
          subSticker2={getSubSticker2(attributes)}
          opecoSticker={getCommercialOperationSticker(attributes)}
          classes={{ container: styles.stickers }}
        />
        <ProductCardOverlay className={styles.overlay}>
          <ProductCardColors
            colors={colorSwatch}
            nbDisplay={7}
            className={classNames(styles.overlayColors, {
              [styles.singleChild]: !sizesList,
            })}
            classes={{
              colorBubble: { container: styles.colorBubbleContainer },
            }}
          />
          {sizesList && <ProductCardSizes sizes={formatSizesList(sizesList)} />}
        </ProductCardOverlay>
      </ProductCardImage>
      <ProductCardDetails className={styles.details}>
        <ProductCardLink to={linkTo} onClick={onClick} />
        <ProductCardColors
          colors={colorSwatch}
          nbDisplay={5}
          className={styles.detailsColors}
          classes={{
            colorBubble: { container: styles.colorBubbleContainer },
          }}
        />
        <div className={styles.labelPriceContainer}>
          <ProductCardLabel
            label={getName(attributes)}
            brand={getBrandName(attributes)}
            className={styles.labelContainer}
          />
          <ProductCardPrice
            price={getFinalPrice(attributes)}
            hasUniqPrice={hasUniqPrice(attributes)}
            discountPrice={getDiscountPrice(attributes)}
            lotQuantity={getLotQuantity(attributes)}
            classes={{ priceFrom: styles.priceFrom }}
          />
        </div>
      </ProductCardDetails>
    </ProductCardContainer>
  );
}

function getProductImages(
  attributes: ProductAttributesType,
  preferCurvy: boolean,
  productName: string,
  linkTo: string,
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
) {
  const imagePaths = [
    getPreferredVisuelFace(attributes, preferCurvy),
    getPreferredVisuelBack(attributes, preferCurvy),
    getPreferredVisuelDetail(attributes, preferCurvy),
  ];

  return imagePaths.reduce<ProductImage[]>((acc, src) => {
    if (src) {
      acc.push({
        alt: productName,
        src,
        linkProps: {
          to: linkTo,
          'aria-hidden': true,
          tabIndex: -1,
          onClick,
        },
      });
    }

    return acc;
  }, []);
}
